/**
 * Packs + Spar-Abo widget (see snippets/pack-subscription-widget.liquid)
 *
 * Keeps the theme's product form in sync with the selected pack tier and purchase type:
 *   - input[name="quantity"]      (form-associated, inside the widget)
 *   - input[name="selling_plan"]  (form-associated, disabled for one-time purchases)
 * All prices are pre-formatted by Liquid and shipped in the JSON block, so no money formatting happens here.
 *
 * Add to cart is performed by the widget itself with an un-patched fetch (taken from a hidden same-origin
 * iframe) and a JSON body. Reason: the Appstle Bundles and Appstle Subscriptions storefront scripts monkey-patch
 * window.fetch / XMLHttpRequest and rewrite add-to-cart requests based on their own (hidden) widget state, which
 * drops the selling plan and overrides the quantity. After a successful add the widget dispatches the same events
 * Focal's <form is="product-form"> would (variant:added, cart:updated, cart:refresh, cart-notification:show).
 */
(function () {
  if (window.customElements.get('pack-subscription-widget')) return;

  class PackSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('script.psw-data').textContent);
      } catch (error) {
        this.data = { onetime: {}, plans: {} };
      }

      this.formId = this.getAttribute('form-id');
      this.qtyInput = this.querySelector('.psw-qty-input');
      this.planInput = this.querySelector('.psw-plan-input');
      this.planSelect = this.querySelector('.psw-plan-select');

      this.addEventListener('change', this._onChange.bind(this));
      this.querySelectorAll('.psw-atc').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this._addToCart();
        });
      });

      const form = this.form;
      if (form && !form.__pswBound) {
        form.__pswBound = true;
        // App widgets (Appstle Bundles / Subscriptions, KaChing) inject their own quantity / selling_plan
        // inputs into the form after load. Disable them whenever they appear and again right before submit,
        // otherwise the last input in the form wins and overrides this widget's choice.
        form.addEventListener('submit', (event) => {
          this._disableForeignInputs();
          this._sync();
          event.preventDefault();
          event.stopImmediatePropagation();
          this._addToCart();
        }, true);
        this._observer = new MutationObserver(() => this._disableForeignInputs());
        this._observer.observe(form, { childList: true, subtree: true });
      }

      this._onVariantChanged = () => this._sync();
      document.addEventListener('variant:changed', this._onVariantChanged);

      this._disableForeignInputs();
      this._sync();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._onVariantChanged) document.removeEventListener('variant:changed', this._onVariantChanged);
      const form = this.form;
      if (form) form.__pswBound = false;
    }

    get form() {
      return document.getElementById(this.formId);
    }

    get quantity() {
      const checked = this.querySelector('.psw-tier__input:checked');
      return checked ? checked.value : (this.qtyInput ? this.qtyInput.value : '1');
    }

    get purchaseType() {
      const checked = this.querySelector('.psw-type-input:checked');
      return checked ? checked.value : 'onetime';
    }

    get planId() {
      if (this.planSelect) return this.planSelect.value;
      return this.planInput ? this.planInput.value : '';
    }

    _onChange(event) {
      // Only react to our own controls, not to bubbling events from app scripts
      if (!event.target.closest('.psw-tier__input, .psw-type-input, .psw-plan-select')) return;
      this._sync();
    }

    _sync() {
      const quantity = this.quantity;
      const type = this.purchaseType;
      const planId = this.planId;

      this.querySelectorAll('.psw-tier').forEach((tier) => {
        const input = tier.querySelector('.psw-tier__input');
        tier.classList.toggle('is-selected', !!(input && input.checked));
      });

      this.querySelectorAll('.psw-option').forEach((option) => {
        option.classList.toggle('is-active', option.dataset.type === type);
      });

      if (this.qtyInput) this.qtyInput.value = quantity;

      if (this.planInput) {
        if (type === 'subscription' && planId) {
          this.planInput.value = planId;
          this.planInput.disabled = false;
        } else {
          this.planInput.value = '';
          this.planInput.disabled = true;
        }
      }

      const onetime = this.data.onetime[quantity] || {};
      this._set('ot-total', onetime.total);
      this._set('ot-compare', onetime.compare);
      this._set('ot-unit', onetime.unit);

      const plan = this.data.plans[planId];
      const subTier = plan ? (plan.tiers[quantity] || {}) : {};
      if (plan) {
        this._set('sub-total', subTier.total);
        this._set('sub-compare', subTier.compare);
        this._set('sub-unit', subTier.unit);
        this._set('sub-pct', plan.pct);
      }

      const active = (type === 'subscription' && plan) ? subTier : onetime;
      this._syncStickyBar(active.total, active.unit);

      this.dispatchEvent(new CustomEvent('pack-subscription:changed', {
        bubbles: true,
        detail: {
          quantity: parseInt(quantity, 10),
          purchaseType: type,
          sellingPlanId: type === 'subscription' ? planId : null
        }
      }));
    }

    /**
     * Un-patched fetch from a hidden same-origin iframe, so app interceptors cannot rewrite the request.
     */
    _pristineFetch() {
      if (!PackSubscriptionWidget._frame || !PackSubscriptionWidget._frame.contentWindow) {
        const frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        frame.tabIndex = -1;
        frame.style.display = 'none';
        document.body.appendChild(frame);
        PackSubscriptionWidget._frame = frame;
      }
      const win = PackSubscriptionWidget._frame.contentWindow;
      return win && win.fetch ? win.fetch.bind(win) : window.fetch.bind(window);
    }

    async _addToCart() {
      if (this._busy) return;
      const form = this.form;
      if (!form) return;

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      this._disableForeignInputs();
      this._sync();

      const variantId = (form.querySelector('[name="id"]') || {}).value;
      if (!variantId) return;

      const item = { id: parseInt(variantId, 10), quantity: parseInt(this.quantity, 10) || 1 };
      if (this.purchaseType === 'subscription' && this.planId) item.selling_plan = parseInt(this.planId, 10);

      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartAddUrl = (routes.cartAddUrl || root + 'cart/add') + '.js';
      const cartUrl = (routes.cartUrl || root + 'cart') + '.js';
      const fetchFn = this._pristineFetch();
      const buttons = Array.from(this.querySelectorAll('.psw-atc'));

      this._busy = true;
      buttons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      });

      let response;
      let responseJson = {};
      try {
        response = await fetchFn(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [item], sections: 'mini-cart' })
        });
        responseJson = await response.json();
      } catch (error) {
        responseJson = { description: String(error) };
      } finally {
        this._busy = false;
        buttons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });
      }

      const ok = !!(response && response.ok);

      if (ok) {
        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = root + 'cart';
          return;
        }

        form.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { variant: responseJson.items ? responseJson.items[0] : responseJson }
        }));

        try {
          const cartContent = await fetchFn(cartUrl).then((res) => res.json());
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartContent }
          }));
          cartContent.sections = responseJson.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: settings.cartType === 'drawer' && form.closest('.drawer') === null
            }
          }));
        } catch (error) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } else {
        form.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      form.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: {
          status: ok ? 'success' : 'error',
          error: responseJson.description || responseJson.message || ''
        }
      }));

      this.dispatchEvent(new CustomEvent('pack-subscription:added', {
        bubbles: true,
        detail: { ok: ok, item: item, response: responseJson }
      }));
    }

    /**
     * Focal's sticky add-to-cart bar shows the single-unit variant price. Show the selected pack total
     * and its unit price instead, for the selected purchase type.
     */
    _syncStickyBar(total, unit) {
      const sticky = document.querySelector('product-sticky-form[form-id="' + this.formId + '"]') || document.querySelector('product-sticky-form');
      if (!sticky) return;
      const price = sticky.querySelector('.product-sticky-form__price');
      if (price && total) price.textContent = total;
      const unitPrice = sticky.querySelector('.product-sticky-form__unit-price .unit-price-measurement__price');
      if (unitPrice && unit) unitPrice.textContent = unit;
    }

    _set(role, value) {
      this.querySelectorAll('[data-psw="' + role + '"]').forEach((el) => {
        el.textContent = value == null ? '' : String(value);
      });
    }

    /**
     * Any other quantity / selling_plan input bound to the same form (theme quantity selector, app widgets)
     * would be submitted alongside ours. Disable them so the widget is the only source of truth.
     */
    _disableForeignInputs() {
      const selector = 'input[name="quantity"], input[name="selling_plan"], select[name="selling_plan"]';
      const candidates = new Set();

      document.querySelectorAll(selector + ',[form="' + this.formId + '"]').forEach((el) => {
        if (el.matches(selector)) candidates.add(el);
      });

      const form = this.form;
      if (form) form.querySelectorAll(selector).forEach((el) => candidates.add(el));

      candidates.forEach((el) => {
        if (this.contains(el)) return;
        const boundForm = el.getAttribute('form') ? document.getElementById(el.getAttribute('form')) : el.form;
        if (boundForm === form) el.disabled = true;
      });
    }
  }

  window.customElements.define('pack-subscription-widget', PackSubscriptionWidget);
})();
